import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/adminApi";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CTAButton } from "@/components/site/CTAButton";
import { ErrorAlert, Field, a11y, inputClass } from "@/components/forms/FormBits";

const EMPTY = { current: "", next: "", confirm: "" };

export const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      setValues(EMPTY);
      setErrors({});
      setError("");
    }
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const found = {};
    if (!values.current) found.current = "Enter your current password.";
    if (values.next.length < 10) found.next = "Use at least 10 characters.";
    if (values.confirm !== values.next) found.confirm = "The new passwords don't match.";
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    setError("");
    try {
      await adminApi.post("/auth/change-password", { current_password: values.current, new_password: values.next });
      toast.success("Password updated", { description: "Use your new password next time you sign in." });
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const input = (key, label, autoComplete) => (
    <Field id={`pw-${key}`} label={label} error={errors[key]}>
      <input
        {...a11y(`pw-${key}`, errors[key], true)}
        type="password"
        autoComplete={autoComplete}
        value={values[key]}
        onChange={set(key)}
        className={`${inputClass} h-11`}
        data-testid={`change-password-${key}-input`}
      />
    </Field>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 px-3.5 text-sm text-white/85 transition-colors hover:bg-white/[0.06]"
          data-testid="change-password-open-button"
        >
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Change password</span>
        </button>
      </DialogTrigger>
      <DialogContent className="border-white/10 bg-[#0E1015] text-white sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">This updates the shared founders login.</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={onSubmit} className="space-y-4" data-testid="change-password-form">
          {input("current", "Current password", "current-password")}
          {input("next", "New password", "new-password")}
          {input("confirm", "Confirm new password", "new-password")}
          {error && <ErrorAlert message={error} testId="change-password-error" />}
          <CTAButton type="submit" disabled={busy} arrow={false} className="w-full" testId="change-password-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {busy ? "Saving…" : "Update password"}
          </CTAButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
